import {
  assemblyTemplate as assemblyTemplateState,
  selectedAssemblyId,
} from '@/components/Analysis/state';
import { useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import {
  Add as AddIcon,
  ArrowDropDown as DownIcon,
  ArrowDropUp as UpIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Divider,
  Icon,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import {
  AssemblyTemplateFieldDataDocument,
  useAssemblyTemplateFieldDataQuery,
  useUpdateScCategoryMutation,
} from '@/__generated__/graphql';
import { abyssColors, SC_CATEGORY_COLORS } from '@/theme/colors';
import { useFeatureFlag } from '@/hooks';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { addButtonStyles, ColorIndicator } from './styles';

const defaultScCategoryColor = 'black';

export const ScCategory = () => {
  const assemblyId = useAtomValue(selectedAssemblyId);
  const assemblyTemplate = useAtomValue(assemblyTemplateState);
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const [anchorElement, setAnchorElement] = useState<undefined | HTMLElement>(undefined);
  const canAccessAitTools = useFeatureFlag(['ait-task-lead', 'ait-engineer', 'admin']);
  const structureId = useGetStructureId();

  const isMenuOpen = Boolean(anchorElement);

  const { data, loading } = useAssemblyTemplateFieldDataQuery({
    variables: { assemblyId: assemblyId ?? '' },
    skip: !assemblyId,
  });

  const [updateScCategory, { loading: updateScCategoryLoading }] = useUpdateScCategoryMutation({
    refetchQueries: [AssemblyTemplateFieldDataDocument],
  });

  const scCategoryField = useMemo(() => {
    return assemblyTemplate?.fields?.find(({ key }) => key === 'governing_sc_category');
  }, [assemblyTemplate]);

  const assemblyScCategory = useMemo(() => {
    return data?.assembly?.templateFieldData?.governing_sc_category;
  }, [data?.assembly?.templateFieldData]);

  const handleUpdateScCategory = async (scCategory: string) => {
    if (updateScCategoryLoading || !assemblyId || !scCategory || !structureId) return;
    setAnchorElement(undefined);
    try {
      await updateScCategory({ variables: { input: { assemblyId, scCategory, structureId } } });
    } catch {
      showSnackBar('Failed to update SC Category', 'error');
    }
  };

  const dropDownIcon = useMemo(() => {
    const IconComponent = isMenuOpen ? UpIcon : DownIcon;
    return (
      <Icon sx={{ fontSize: 12 }}>
        <IconComponent sx={{ fontSize: 20, mt: '-4px', ml: '-4px' }} />
      </Icon>
    );
  }, [isMenuOpen]);

  if (!scCategoryField || scCategoryField.isHidden || loading) return <></>;

  return (
    <>
      {assemblyScCategory ? (
        <Stack direction="row" spacing={1} alignItems="center" my={2}>
          <Box>
            <Typography sx={{ width: 'fit-content', color: 'info.main', fontSize: '12px' }}>
              SC Category
            </Typography>
          </Box>
          <ButtonBase
            disabled={!canAccessAitTools || updateScCategoryLoading}
            onClick={(event) => setAnchorElement(event.currentTarget)}
          >
            <Stack
              direction="row"
              alignItems="center"
              sx={{
                border: '1px solid',
                px: 1,
                py: 0.36,
                borderRadius: 1,
                cursor: 'pointer',
                borderColor: abyssColors.primary[500],
              }}
            >
              <ColorIndicator
                sx={{
                  mr: 0.8,
                  backgroundColor: SC_CATEGORY_COLORS[assemblyScCategory] ?? defaultScCategoryColor,
                }}
              />
              <Typography fontSize="12px" lineHeight="18px">
                {assemblyScCategory}
              </Typography>
              {canAccessAitTools && (
                <>
                  <Divider
                    flexItem
                    orientation="vertical"
                    sx={{ mx: 1, borderColor: abyssColors.primary[500] }}
                  />
                  {updateScCategoryLoading ? <CircularProgress size={18} /> : <>{dropDownIcon}</>}
                </>
              )}
            </Stack>
          </ButtonBase>
        </Stack>
      ) : (
        <Button
          size="small"
          variant="outlined"
          sx={addButtonStyles}
          startIcon={<AddIcon />}
          onClick={(event) => setAnchorElement(event.currentTarget)}
        >
          Add SC Category
        </Button>
      )}
      {isMenuOpen && (
        <Menu
          anchorEl={anchorElement}
          open={isMenuOpen}
          onClose={() => setAnchorElement(undefined)}
          sx={{ mt: 0.2 }}
        >
          {scCategoryField.options?.map((option) => (
            <MenuItem
              key={option}
              sx={{ width: 152, py: 0 }}
              onClick={() => handleUpdateScCategory(option)}
            >
              <ListItemIcon>
                <ColorIndicator
                  sx={{ backgroundColor: SC_CATEGORY_COLORS[option] ?? defaultScCategoryColor }}
                />
              </ListItemIcon>
              <ListItemText>{option}</ListItemText>
            </MenuItem>
          ))}
        </Menu>
      )}
    </>
  );
};
